import React, { useEffect, useMemo, useState } from "react";

const MANIFEST_URL = "/cancellation-forms/manifest.json";

const normalizeKey = (value) =>
  value.toLowerCase().replace(/[\s\-_&()]/g, "");

const toBlockKey = (name) => {
  const first = name.trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(first) ? first : "#";
};

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const statusConfig = {
  READY: {
    color: "#34f6a5",
    glow: "rgba(52, 246, 165, 0.42)",
    background: "rgba(52, 246, 165, 0.1)",
    label: "READY",
  },
  CAUTION: {
    color: "#f3c64c",
    glow: "rgba(243, 198, 76, 0.38)",
    background: "rgba(243, 198, 76, 0.11)",
    label: "CAUTION",
  },
  RESTRICTED: {
    color: "#ff6b6b",
    glow: "rgba(255, 107, 107, 0.35)",
    background: "rgba(255, 107, 107, 0.1)",
    label: "RESTRICTED",
  },
  COMING_SOON: {
    color: "#9aa3b2",
    glow: "rgba(154, 163, 178, 0.28)",
    background: "rgba(154, 163, 178, 0.1)",
    label: "COMING SOON",
  },
};

const commandStatuses = [
  "Database Online",
  "PDF Library Connected",
  "Secure Access",
  "Forms Synced",
];

const getInitials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const getCarrierStatus = (carrier) => (carrier.url ? "READY" : "COMING_SOON");

const StatusBadge = ({ status = "READY" }) => {
  const cfg = statusConfig[status] || statusConfig.READY;

  return (
    <span
      className="cfd-status-badge"
      style={{
        "--badge-color": cfg.color,
        "--badge-glow": cfg.glow,
        "--badge-bg": cfg.background,
      }}
    >
      <span className="cfd-status-dot" />
      {cfg.label}
    </span>
  );
};

const CarrierCard = ({ carrier, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [clicked, setClicked] = useState(false);
  const status = getCarrierStatus(carrier);
  const hasPdf = status === "READY";

  const handleView = () => {
    if (!hasPdf) return;
    setClicked(true);
    setTimeout(() => setClicked(false), 240);
    window.open(carrier.url, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      className="cfd-card"
      style={{ "--entry-delay": `${Math.min(index, 18) * 34}ms` }}
    >
      <div className="cfd-card-sheen" />
      <div className="cfd-card-top">
        <div className="cfd-carrier-mark" aria-hidden="true">
          <span>{getInitials(carrier.name)}</span>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="cfd-carrier-copy">
        <span className="cfd-carrier-kicker">Carrier Document</span>
        <h2>{carrier.name}</h2>
      </div>

      <button
        type="button"
        onClick={handleView}
        disabled={!hasPdf}
        className={`cfd-view-button ${clicked ? "is-clicked" : ""}`}
      >
        <span>{hasPdf ? "VIEW PDF" : "PDF NOT AVAILABLE"}</span>
      </button>

      <div className="cfd-card-footer">
        <span>PDF Ready</span>
        <span>Updated</span>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="cfd-notes-toggle"
          aria-expanded={expanded}
        >
          Optional Notes
          <span>{expanded ? "-" : "+"}</span>
        </button>
      </div>

      <div className={`cfd-notes-panel ${expanded ? "is-open" : ""}`}>
        <textarea
          rows={3}
          placeholder="Add notes..."
          aria-label={`${carrier.name} optional notes`}
        />
      </div>
    </article>
  );
};

const buildUniqueCarriers = (forms = []) => {
  const seen = new Set();

  return forms
    .filter((form) => form?.name && form?.filename && form?.url)
    .map((form) => ({
      name: form.name,
      filename: form.filename,
      key: form.key || normalizeKey(form.name),
      url: form.url,
      block: toBlockKey(form.name),
    }))
    .filter((form) => {
      if (seen.has(form.key)) return false;
      seen.add(form.key);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const CancelationFormDirectory = () => {
  const [search, setSearch] = useState("");
  const [activeLetter, setActiveLetter] = useState(null);
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    let isMounted = true;

    fetch(MANIFEST_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load cancellation forms: ${response.status}`);
        }
        return response.json();
      })
      .then((manifest) => {
        if (isMounted) {
          setCarriers(buildUniqueCarriers(manifest.forms));
        }
      })
      .catch(() => {
        if (isMounted) {
          setCarriers([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const activeLetters = useMemo(
    () => [...new Set(carriers.map((carrier) => carrier.block))],
    [carriers]
  );

  const filteredCarriers = useMemo(
    () =>
      carriers.filter((carrier) => {
        const normalizedSearch = normalizeKey(search);
        const matchSearch =
          !normalizedSearch ||
          normalizeKey(carrier.name).includes(normalizedSearch) ||
          normalizeKey(carrier.filename).includes(normalizedSearch);
        const matchLetter = activeLetter ? carrier.block === activeLetter : true;
        return matchSearch && matchLetter;
      }),
    [activeLetter, carriers, search]
  );

  return (
    <div className="cfd-page">
      <style>{`
        .cfd-page,
        .cfd-page * {
          box-sizing: border-box;
        }

        .cfd-page {
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
          color: #f3f7ff;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          background:
            radial-gradient(circle at 14% 10%, rgba(0, 221, 255, 0.16), transparent 28%),
            radial-gradient(circle at 88% 18%, rgba(243, 198, 76, 0.13), transparent 26%),
            linear-gradient(145deg, #061013 0%, #09181f 48%, #05070a 100%);
        }

        .cfd-page::before,
        .cfd-page::after {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }

        .cfd-page::before {
          opacity: 0.28;
          background-image:
            linear-gradient(rgba(79, 229, 255, 0.09) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79, 229, 255, 0.09) 1px, transparent 1px);
          background-size: 42px 42px;
          mask-image: linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.18));
          animation: cfd-grid-drift 18s linear infinite;
        }

        .cfd-page::after {
          opacity: 0.18;
          background:
            linear-gradient(180deg, transparent 0%, rgba(0, 244, 255, 0.12) 48%, transparent 52%),
            radial-gradient(circle at 70% 72%, rgba(0, 221, 255, 0.16), transparent 24%);
          background-size: 100% 260px, 100% 100%;
          animation: cfd-scan 7s ease-in-out infinite;
        }

        .cfd-shell {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 1520px;
          margin: 0 auto;
          padding: clamp(22px, 3vw, 44px);
        }

        .cfd-command-header {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(82, 238, 255, 0.24);
          border-radius: 18px;
          padding: clamp(24px, 4vw, 44px);
          background:
            linear-gradient(135deg, rgba(10, 25, 31, 0.92), rgba(10, 17, 24, 0.7)),
            radial-gradient(circle at 88% 24%, rgba(244, 199, 76, 0.14), transparent 28%);
          box-shadow:
            0 22px 80px rgba(0, 0, 0, 0.38),
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0 0 32px rgba(0, 221, 255, 0.08);
        }

        .cfd-command-header::before {
          content: "";
          position: absolute;
          inset: -40%;
          background: conic-gradient(from 120deg, transparent, rgba(0, 221, 255, 0.16), transparent, rgba(244, 199, 76, 0.12), transparent);
          animation: cfd-mesh 13s linear infinite;
          opacity: 0.38;
        }

        .cfd-command-header > * {
          position: relative;
          z-index: 1;
        }

        .cfd-header-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 24px;
          align-items: end;
        }

        .cfd-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
          color: #f3c64c;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }

        .cfd-eyebrow span {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: #34f6a5;
          box-shadow: 0 0 18px rgba(52, 246, 165, 0.72);
          animation: cfd-pulse 1.8s ease-in-out infinite;
        }

        .cfd-command-header h1 {
          max-width: 980px;
          margin: 0;
          color: #f8fbff;
          font-size: clamp(2rem, 5vw, 4.4rem);
          line-height: 0.94;
          letter-spacing: 0.035em;
          text-transform: uppercase;
          text-shadow: 0 0 32px rgba(0, 221, 255, 0.22);
        }

        .cfd-command-header p {
          max-width: 760px;
          margin: 16px 0 0;
          color: rgba(224, 239, 255, 0.74);
          font-size: clamp(0.9rem, 1.3vw, 1.02rem);
          line-height: 1.65;
        }

        .cfd-count-module {
          min-width: 170px;
          padding: 18px;
          border-radius: 16px;
          border: 1px solid rgba(243, 198, 76, 0.28);
          background: rgba(4, 9, 13, 0.48);
          box-shadow: inset 0 0 26px rgba(243, 198, 76, 0.07);
          text-align: right;
        }

        .cfd-count-module strong {
          display: block;
          color: #f3c64c;
          font-size: clamp(2rem, 4vw, 3.4rem);
          line-height: 1;
        }

        .cfd-count-module span {
          color: rgba(240, 247, 255, 0.58);
          font-size: 0.68rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .cfd-status-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 28px;
        }

        .cfd-system-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 34px;
          padding: 8px 12px;
          border: 1px solid rgba(82, 238, 255, 0.18);
          border-radius: 999px;
          background: rgba(5, 18, 24, 0.66);
          color: rgba(228, 249, 255, 0.78);
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          box-shadow: inset 0 0 16px rgba(0, 221, 255, 0.05);
        }

        .cfd-system-pill span {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: #34f6a5;
          box-shadow: 0 0 12px rgba(52, 246, 165, 0.8);
          animation: cfd-pulse 2s ease-in-out infinite;
        }

        .cfd-controls {
          display: grid;
          gap: 18px;
          margin: clamp(22px, 3vw, 34px) 0;
        }

        .cfd-search-wrap {
          position: relative;
        }

        .cfd-search-icon,
        .cfd-clear-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1;
        }

        .cfd-search-icon {
          left: 18px;
          color: rgba(82, 238, 255, 0.78);
          filter: drop-shadow(0 0 10px rgba(82, 238, 255, 0.36));
        }

        .cfd-clear-button {
          right: 12px;
          width: 34px;
          height: 34px;
          border: 1px solid rgba(243, 198, 76, 0.22);
          border-radius: 999px;
          background: rgba(243, 198, 76, 0.08);
          color: #f3c64c;
          cursor: pointer;
          transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
        }

        .cfd-clear-button:hover {
          transform: translateY(-50%) scale(1.04);
          border-color: rgba(243, 198, 76, 0.6);
          box-shadow: 0 0 18px rgba(243, 198, 76, 0.18);
        }

        .cfd-search-input {
          width: 100%;
          min-height: 58px;
          border: 1px solid rgba(82, 238, 255, 0.2);
          border-radius: 16px;
          padding: 0 58px;
          outline: none;
          background: rgba(4, 12, 17, 0.72);
          color: #eef8ff;
          font: inherit;
          font-size: clamp(0.82rem, 1.2vw, 0.95rem);
          letter-spacing: 0.04em;
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.05),
            0 14px 44px rgba(0, 0, 0, 0.24);
          transition: border-color 220ms ease, box-shadow 220ms ease, background 220ms ease;
        }

        .cfd-search-input::placeholder {
          color: rgba(217, 234, 245, 0.42);
        }

        .cfd-search-input:focus {
          border-color: rgba(82, 238, 255, 0.62);
          background: rgba(4, 16, 22, 0.9);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0 0 0 4px rgba(82, 238, 255, 0.08),
            0 0 36px rgba(82, 238, 255, 0.12);
        }

        .cfd-alpha-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .cfd-alpha-button {
          min-width: 40px;
          height: 34px;
          padding: 0 13px;
          border-radius: 999px;
          border: 1px solid rgba(82, 238, 255, 0.15);
          background: rgba(6, 18, 24, 0.7);
          color: rgba(219, 244, 255, 0.62);
          font: inherit;
          font-size: 0.75rem;
          font-weight: 800;
          cursor: pointer;
          transition: transform 180ms ease, color 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
        }

        .cfd-alpha-button:hover:not(:disabled) {
          transform: translateY(-2px);
          color: #e9fbff;
          border-color: rgba(82, 238, 255, 0.58);
          box-shadow: 0 0 20px rgba(82, 238, 255, 0.16);
        }

        .cfd-alpha-button.is-selected {
          color: #091015;
          border-color: rgba(243, 198, 76, 0.78);
          background: linear-gradient(135deg, #f3c64c, #b9902b);
          box-shadow: 0 0 24px rgba(243, 198, 76, 0.26);
          animation: cfd-gold-ring 2.2s ease-in-out infinite;
        }

        .cfd-alpha-button:disabled {
          color: rgba(144, 156, 166, 0.28);
          border-color: rgba(144, 156, 166, 0.08);
          background: rgba(6, 10, 13, 0.36);
          cursor: default;
        }

        .cfd-grid-head {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: end;
          margin-bottom: 16px;
          color: rgba(228, 249, 255, 0.62);
          font-size: 0.72rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .cfd-grid-head strong {
          color: #f3c64c;
        }

        .cfd-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: clamp(14px, 1.45vw, 20px);
          align-items: stretch;
        }

        .cfd-card {
          position: relative;
          overflow: hidden;
          min-width: 0;
          min-height: 286px;
          display: flex;
          flex-direction: column;
          gap: 18px;
          border: 1px solid rgba(82, 238, 255, 0.18);
          border-radius: 18px;
          padding: 18px;
          background:
            linear-gradient(145deg, rgba(13, 30, 39, 0.84), rgba(6, 12, 17, 0.76)),
            radial-gradient(circle at 20% 0%, rgba(82, 238, 255, 0.1), transparent 34%);
          box-shadow:
            0 18px 48px rgba(0, 0, 0, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.06);
          animation: cfd-card-in 520ms ease both;
          animation-delay: var(--entry-delay);
          transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease, background 220ms ease;
        }

        .cfd-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1px;
          background: linear-gradient(120deg, transparent, rgba(82, 238, 255, 0.42), transparent, rgba(243, 198, 76, 0.32), transparent);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 220ms ease;
        }

        .cfd-card:hover {
          transform: translateY(-5px);
          border-color: rgba(82, 238, 255, 0.42);
          box-shadow:
            0 24px 70px rgba(0, 0, 0, 0.36),
            0 0 28px rgba(82, 238, 255, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.09);
        }

        .cfd-card:hover::before,
        .cfd-card:hover .cfd-card-sheen {
          opacity: 1;
        }

        .cfd-card-sheen {
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.08) 45%, transparent 64%);
          transform: translateX(-120%);
          opacity: 0;
          transition: opacity 220ms ease;
          animation: cfd-sheen 2.7s ease-in-out infinite;
          pointer-events: none;
        }

        .cfd-card-top,
        .cfd-card-footer {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .cfd-carrier-mark {
          width: 48px;
          height: 48px;
          flex: 0 0 48px;
          display: grid;
          place-items: center;
          border: 1px solid rgba(82, 238, 255, 0.36);
          border-radius: 15px;
          background:
            radial-gradient(circle at 50% 20%, rgba(82, 238, 255, 0.28), transparent 54%),
            rgba(0, 221, 255, 0.09);
          color: #e8fbff;
          font-size: 0.82rem;
          font-weight: 900;
          box-shadow:
            0 0 24px rgba(82, 238, 255, 0.16),
            inset 0 0 18px rgba(82, 238, 255, 0.08);
        }

        .cfd-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
          border: 1px solid color-mix(in srgb, var(--badge-color) 42%, transparent);
          border-radius: 999px;
          padding: 6px 9px;
          background: var(--badge-bg);
          color: var(--badge-color);
          font-size: 0.64rem;
          font-weight: 900;
          letter-spacing: 0.12em;
          white-space: nowrap;
          box-shadow: 0 0 18px var(--badge-glow);
          animation: cfd-badge-pulse 2.6s ease-in-out infinite;
        }

        .cfd-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--badge-color);
          box-shadow: 0 0 12px var(--badge-color);
        }

        .cfd-carrier-copy {
          position: relative;
          z-index: 1;
          flex: 1;
        }

        .cfd-carrier-kicker {
          display: block;
          margin-bottom: 8px;
          color: rgba(82, 238, 255, 0.66);
          font-size: 0.64rem;
          font-weight: 900;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .cfd-carrier-copy h2 {
          margin: 0;
          color: #f8fbff;
          font-size: clamp(1rem, 1.2vw, 1.18rem);
          line-height: 1.22;
          letter-spacing: 0.02em;
          overflow-wrap: anywhere;
        }

        .cfd-view-button {
          position: relative;
          z-index: 1;
          overflow: hidden;
          width: 100%;
          min-height: 48px;
          border: 1px solid rgba(243, 198, 76, 0.56);
          border-radius: 13px;
          background:
            linear-gradient(135deg, rgba(243, 198, 76, 0.95), rgba(185, 144, 43, 0.94)),
            radial-gradient(circle at 30% 10%, rgba(255, 255, 255, 0.26), transparent 45%);
          color: #081014;
          cursor: pointer;
          font: inherit;
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.16em;
          box-shadow:
            0 14px 34px rgba(243, 198, 76, 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.26);
          transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
        }

        .cfd-view-button::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent, rgba(255, 255, 255, 0.46), transparent);
          transform: translateX(-120%);
          transition: transform 360ms ease;
        }

        .cfd-view-button:hover:not(:disabled) {
          transform: scale(1.02);
          filter: brightness(1.05);
          box-shadow:
            0 18px 42px rgba(243, 198, 76, 0.3),
            0 0 24px rgba(243, 198, 76, 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.32);
        }

        .cfd-view-button:hover:not(:disabled)::before {
          transform: translateX(120%);
        }

        .cfd-view-button.is-clicked {
          transform: scale(0.98);
        }

        .cfd-view-button:disabled {
          cursor: not-allowed;
          color: rgba(239, 246, 255, 0.46);
          border-color: rgba(154, 163, 178, 0.22);
          background: rgba(154, 163, 178, 0.1);
          box-shadow: none;
        }

        .cfd-card-footer {
          color: rgba(224, 239, 255, 0.46);
          font-size: 0.62rem;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .cfd-notes-toggle {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          background: transparent;
          color: rgba(82, 238, 255, 0.66);
          cursor: pointer;
          font: inherit;
          font-size: 0.62rem;
          font-weight: 900;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 0;
          transition: color 180ms ease, text-shadow 180ms ease;
        }

        .cfd-notes-toggle:hover {
          color: #e8fbff;
          text-shadow: 0 0 12px rgba(82, 238, 255, 0.38);
        }

        .cfd-notes-panel {
          position: relative;
          z-index: 1;
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 240ms ease, opacity 200ms ease;
        }

        .cfd-notes-panel.is-open {
          max-height: 110px;
          opacity: 1;
        }

        .cfd-notes-panel textarea {
          width: 100%;
          min-height: 76px;
          margin-top: 2px;
          border: 1px solid rgba(82, 238, 255, 0.16);
          border-radius: 12px;
          outline: none;
          resize: none;
          background: rgba(2, 8, 12, 0.72);
          color: rgba(235, 249, 255, 0.82);
          padding: 10px;
          font: inherit;
          font-size: 0.75rem;
        }

        .cfd-empty {
          min-height: 220px;
          display: grid;
          place-items: center;
          border: 1px dashed rgba(82, 238, 255, 0.18);
          border-radius: 18px;
          color: rgba(224, 239, 255, 0.38);
          background: rgba(4, 12, 17, 0.38);
          text-align: center;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        @keyframes cfd-grid-drift {
          from { background-position: 0 0; }
          to { background-position: 42px 42px; }
        }

        @keyframes cfd-scan {
          0%, 100% { background-position: 0 -260px, 0 0; }
          50% { background-position: 0 100vh, 0 0; }
        }

        @keyframes cfd-mesh {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes cfd-pulse {
          0%, 100% { opacity: 0.72; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.16); }
        }

        @keyframes cfd-gold-ring {
          0%, 100% { box-shadow: 0 0 18px rgba(243, 198, 76, 0.18); }
          50% { box-shadow: 0 0 30px rgba(243, 198, 76, 0.34); }
        }

        @keyframes cfd-badge-pulse {
          0%, 100% { box-shadow: 0 0 12px var(--badge-glow); }
          50% { box-shadow: 0 0 24px var(--badge-glow); }
        }

        @keyframes cfd-card-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes cfd-sheen {
          0%, 45% { transform: translateX(-120%); }
          75%, 100% { transform: translateX(120%); }
        }

        @media (max-width: 900px) {
          .cfd-header-grid {
            grid-template-columns: 1fr;
          }

          .cfd-count-module {
            text-align: left;
            width: fit-content;
          }

          .cfd-card-grid {
            grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
          }
        }

        @media (max-width: 560px) {
          .cfd-shell {
            padding: 18px 14px 28px;
          }

          .cfd-command-header {
            border-radius: 15px;
            padding: 22px 16px;
          }

          .cfd-status-strip {
            gap: 8px;
          }

          .cfd-system-pill {
            width: 100%;
          }

          .cfd-search-input {
            min-height: 54px;
            padding-left: 48px;
            padding-right: 50px;
          }

          .cfd-alpha-button {
            min-width: 36px;
            height: 32px;
          }

          .cfd-grid-head {
            flex-direction: column;
            align-items: flex-start;
          }

          .cfd-card-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .cfd-page::before,
          .cfd-page::after,
          .cfd-command-header::before,
          .cfd-eyebrow span,
          .cfd-system-pill span,
          .cfd-alpha-button.is-selected,
          .cfd-status-badge,
          .cfd-card,
          .cfd-card-sheen {
            animation: none !important;
          }

          .cfd-card,
          .cfd-view-button,
          .cfd-alpha-button,
          .cfd-clear-button,
          .cfd-notes-panel {
            transition: none !important;
          }
        }
      `}</style>

      <main className="cfd-shell">
        <section className="cfd-command-header" aria-labelledby="cfd-title">
          <div className="cfd-header-grid">
            <div>
              <div className="cfd-eyebrow">
                <span />
                Secure Form Terminal
              </div>
              <h1 id="cfd-title">Carrier Cancellation Command Center</h1>
              <p>
                Secure repository for all carrier cancellation and surrender
                documentation.
              </p>
            </div>
            <div className="cfd-count-module" aria-label={`${carriers.length} PDF forms`}>
              <strong>{carriers.length}</strong>
              <span>PDF Forms Synced</span>
            </div>
          </div>

          <div className="cfd-status-strip">
            {commandStatuses.map((status) => (
              <span className="cfd-system-pill" key={status}>
                <span />
                {status}
              </span>
            ))}
          </div>
        </section>

        <section className="cfd-controls" aria-label="Carrier search and filters">
          <div className="cfd-search-wrap">
            <span className="cfd-search-icon" aria-hidden="true">
              SEARCH
            </span>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search carrier..."
              className="cfd-search-input"
              aria-label="Search carrier"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="cfd-clear-button"
                aria-label="Clear search"
              >
                X
              </button>
            )}
          </div>

          <div className="cfd-alpha-row" aria-label="Alphabet filter">
            {alphabet.map((letter) => {
              const isActive = activeLetters.includes(letter);
              const isSelected = activeLetter === letter;

              return (
                <button
                  key={letter}
                  type="button"
                  disabled={!isActive}
                  onClick={() =>
                    isActive ? setActiveLetter(isSelected ? null : letter) : null
                  }
                  className={`cfd-alpha-button ${isSelected ? "is-selected" : ""}`}
                  aria-pressed={isSelected}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </section>

        <section aria-label="Carrier cancellation forms">
          <div className="cfd-grid-head">
            <span>
              Showing <strong>{filteredCarriers.length}</strong> carrier forms
            </span>
            <span>PDF Library / Cancellation Forms</span>
          </div>

          {filteredCarriers.length > 0 ? (
            <div className="cfd-card-grid">
              {filteredCarriers.map((carrier, index) => (
                <CarrierCard key={carrier.key} carrier={carrier} index={index} />
              ))}
            </div>
          ) : (
            <div className="cfd-empty">
              No carriers found. Adjust search parameters.
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CancelationFormDirectory;
